import { zodResolver } from '@hookform/resolvers/zod';
import { useForm } from 'react-hook-form';
import { useQueryClient } from 'react-query';
import * as z from 'zod';
import Time from '@/components/Time';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Form, FormField, FormItem, FormControl, FormMessage } from '@/components/ui/form';
import { Textarea } from '@/components/ui/textarea';
import useNotes from '@/hooks/useNotes';
import axiosClient from '@/utils/axiosClient';

const schema = z.object({
  content: z.string().nonempty({ message: 'Note is required' }),
});

type FormData = z.infer<typeof schema>;

interface NotesProps {
  contactId: string;
}

const Notes = ({ contactId }: NotesProps) => {
  const notes = useNotes(contactId);
  const queryClient = useQueryClient();

  const form = useForm<FormData>({
    resolver: zodResolver(schema),
    defaultValues: {
      content: '',
    },
  });

  const onSubmit = async (data: FormData) => {
    try {
      await axiosClient.post(`v1/notes`, {
        content: data.content,
        contact_id: contactId,
      });

      queryClient.invalidateQueries(`v1/notes/${contactId}`);
      form.reset();
    } catch (error) {
      console.error('Error saving note:', error);
    }
  };

  return (
    <>
      <Card>
        <CardHeader>
          <CardTitle>Notes</CardTitle>
          <CardDescription>Recent notes and comments</CardDescription>
        </CardHeader>
        <CardContent>
          <Form {...form}>
            <form onSubmit={form.handleSubmit(onSubmit)} className="flex flex-col justify-center gap-4 ">
              <FormField
                control={form.control}
                name="content"
                render={({ field }) => (
                  <FormItem className="w-full">
                    <FormControl>
                      <Textarea {...field} placeholder="Leave a note on the contact" />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />

              <Button>Add Note</Button>
            </form>
          </Form>
          <div className="mt-4 space-y-4 ">
            {notes?.data?.map((note) => (
              <div key={note.id} className="p-3 bg-gray-100 rounded-lg">
                <p className="font-medium">{note.content}</p>
                <p className="mt-1 text-xs text-gray-500">
                  {note.user.name} - <Time>{note.created_at}</Time>
                </p>
              </div>
            ))}
          </div>
        </CardContent>
      </Card>
    </>
  );
};

export default Notes;
